import React from 'react';
import { Link } from 'react-router-dom';
import { appSignupUrl } from '../../config';
import { isLive } from '../../config/liveRoutes';
import { usePageMeta } from '../../hooks/usePageMeta';
import NgShell from '../../components/ng/NgShell';
import NgReveal from '../../components/ng/NgReveal';
import NgEventPreview from '../../components/ng/NgEventPreview';
import GuestCollectionScene from '../../components/ng/stages/GuestCollectionScene';
import ClientWindowStage from '../../components/ng/stages/ClientWindowStage';
import '../../styles/ng-home.css';

const PAGE_TITLE = 'Free Event Software for Planners in Nigeria | Comtev';
const PAGE_DESCRIPTION =
    'Free event software for Nigerian planners. Collect guest lists on WhatsApp, send QR invitations, manage check-in and invoice clients in naira. Try Comtev.';

/* NG capabilities were confirmed by the product owner on 2026-08-18.
   This market has its own guest collection, e-access and invoicing offer.
   No naira amounts here: approved rates live in pricingTiers.ts and /pricing. */
const QA_ITEMS: { q: string; a: string }[] = [
    { q: 'What is free, and what do I pay for?', a: 'Your account, clients and events are free to run. You pay for the messages you send and the guest-collection seats you use at wholesale rates. You set the price you charge your client for those services.' },
    { q: 'Does the family need to install an app?', a: 'No. The people contributing guest names talk to the Comtev AI assistant on WhatsApp. It collects their lists, follows up and keeps them within the guest allowance you set.' },
    { q: 'What does my client see?', a: 'A private, PIN-protected link with the event details you choose to share: guest lists, confirmations, invoices and check-ins. Your wholesale costs and margins stay private.' },
    { q: 'How does check-in work?', a: 'Guests receive a QR access pass with their invitation. Your authorised door staff scan it with a phone. If you remove a guest, their old pass is revoked.' },
    { q: 'How do I receive payments?', a: 'Send your client an invoice in naira. They pay through Paystack from their private link, without creating an account. You can withdraw your balance to your Nigerian bank account.' },
];

const NgHomePage: React.FC = () => {
    usePageMeta({
        title: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
        canonicalPath: '/',
        jsonLd: [
            {
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'Comtev for event planners',
                applicationCategory: 'BusinessApplication',
                operatingSystem: 'Web, iOS, Android',
                description: 'Free event software for Nigerian planners. AI guest collection over WhatsApp, QR invitations, check-in, team permissions, automations and invoicing in naira.',
                /* No headline price: sends and collection seats are separate services. */
            },
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: QA_ITEMS.map(({ q, a }) => ({
                    '@type': 'Question', name: q,
                    acceptedAnswer: { '@type': 'Answer', text: a },
                })),
            },
        ],
    });

    return (
        <NgShell ctaTag="ng-nav-start-free" className="ng-home" footerTagline="Guest lists. Invites. Check-in. Invoices. One place to keep the details together.">
            <a className="ngh-skip" href="#ng-main">Skip to content</a>
            <main id="ng-main">
                <section className="ngh-hero" aria-labelledby="ng-hero-title">
                    <div className="ngh-wrap">
                        <p className="ngh-category"><span aria-hidden="true">✳</span> Free event software for Nigerian planners</p>
                        <h1 id="ng-hero-title">Plan the event.<br />Leave the <mark>chasing.</mark></h1>
                        <p className="ngh-hero-copy">Guest names collected on WhatsApp. Invitations sent.<br className="ngh-desktop-break" /> Comtev keeps the list moving, so you can get on with the event.</p>
                        <div className="ngh-actions">
                            <a href={appSignupUrl()} className="ngl-btn ngh-primary" data-cta="ng-hero-start-free">Start free <span aria-hidden="true">↗</span></a>
                            <a href="#try-comtev" className="ngh-link">Take a look <span aria-hidden="true">↓</span></a>
                        </div>
                        <p className="ngh-fine">No subscription. Pay only for messages and collection seats.</p>
                        <div className="ngh-hero-doodle ngh-doodle-left" aria-hidden="true">
                            <svg viewBox="0 0 150 170" fill="none"><path d="M70 118C47 105 17 78 30 44C44 12 98 12 114 46C129 79 96 108 76 119L80 130L67 129Z" /><path d="M75 130C51 147 105 146 74 168M41 52C43 39 49 35 55 32M67 125L80 124" /></svg>
                            <span>A little less<br />on your plate.</span>
                        </div>
                        <div className="ngh-hero-doodle ngh-doodle-right" aria-hidden="true">
                            <svg viewBox="0 0 160 130" fill="none"><path d="M28 64L59 90L119 26M16 68C11 14 108 1 141 44C177 91 116 126 65 115C36 109 13 92 16 68ZM124 101L144 116M144 82L159 85M108 119L114 130" /></svg>
                            <span>On the list.<br />Off your mind.</span>
                        </div>
                    </div>
                </section>

                <section id="try-comtev" className="ngh-preview-section" aria-label="Explore an example event">
                    <div className="ngh-wrap">
                        <NgEventPreview />
                        <p className="ngh-preview-caption">From the first name to the last guest through the door.</p>
                    </div>
                </section>

                <div className="ngh-use-strip ngh-wrap" aria-label="For all kinds of events">
                    <span>For the events you make happen</span>
                    <p>Weddings <i aria-hidden="true">✳</i> Birthdays <i aria-hidden="true">✳</i> Celebrations <i aria-hidden="true">✳</i> Client events</p>
                </div>

                <section className="ngh-section ngh-wrap" aria-labelledby="ng-list-title">
                    <NgReveal>
                        <div className="ngh-feature ngh-feature-list">
                            <div className="ngh-feature-copy">
                                <p className="ngh-label">01 / Bring the names together</p>
                                <h2 id="ng-list-title">One guest list.<br />Even when everyone<br />has <mark>their own.</mark></h2>
                                <p>The bride has a list. Her mum has another. Give each person an allowance and let our AI assistant collect their names on WhatsApp.</p>
                                <p>Comtev follows up, brings the lists together and handles duplicates. You keep the final count.</p>
                                <p className="ngh-feature-foot"><span aria-hidden="true">↳</span> They use WhatsApp. You get your evening back.</p>
                            </div>
                            <figure className="ngh-scene">
                                <GuestCollectionScene />
                                <figcaption>Illustrative guest collection conversation</figcaption>
                            </figure>
                        </div>
                    </NgReveal>
                </section>

                <section className="ngh-section ngh-wrap" aria-labelledby="ng-door-title">
                    <NgReveal>
                        <div className="ngh-door">
                            <div className="ngh-door-art" aria-hidden="true">
                                <div className="ngh-pass"><span>YOU’RE ON THE LIST</span><strong>Ada &amp; Emeka</strong><p>Saturday · Lagos</p><div className="ngh-qr"><svg viewBox="0 0 100 100"><path d="M4 4h26v26H4zM70 4h26v26H70zM4 70h26v26H4z" fill="none" stroke="currentColor" strokeWidth="8"/><path d="M12 12h10v10H12zM78 12h10v10H78zM12 78h10v10H12zM40 4h10v16H40zM54 24h10v20H54zM38 30h10v24H38zM4 42h20v10H4zM24 54h12v10H24zM52 54h12v12H52zM70 40h26v10H70zM78 54h10v22H78zM38 74h12v22H38zM54 82h14v14H54zM70 86h26v10H70zM88 68h8v12H88z" fill="currentColor"/></svg></div><p>Ngozi Okafor · Guest +1</p><span className="ngh-pass-bottom">INVITATION &amp; ACCESS PASS</span></div>
                                <div className="ngh-pass-stamp">✓ You’re in.</div>
                                <svg className="ngh-pass-arrow" viewBox="0 0 160 90" fill="none"><path d="M5 8C40 90 120 89 148 21M126 29L149 17L158 40" /></svg>
                            </div>
                            <div className="ngh-feature-copy">
                                <p className="ngh-label">02 / From invitation to entrance</p>
                                <h2 id="ng-door-title">Good evening.<br />You’re <mark>expected.</mark></h2>
                                <p>Send invitations and QR access passes straight to your guests. At the door, your team scans with a phone and sees who’s arrived.</p>
                                <p>Remove a guest and their old pass is revoked. Give door staff scanning access without opening up the rest of your account.</p>
                                <a href="#try-comtev" className="ngh-link">Explore the event example <span aria-hidden="true">↗</span></a>
                            </div>
                        </div>
                    </NgReveal>
                </section>

                <section className="ngh-section ngh-wrap" aria-labelledby="ng-client-title">
                    <NgReveal>
                        <div className="ngh-feature ngh-feature-client">
                            <div className="ngh-feature-copy">
                                <p className="ngh-label">03 / Keep your client in the loop</p>
                                <h2 id="ng-client-title">An update.<br />Without <mark>the call.</mark></h2>
                                <p>Give your client a private, PIN-protected link. They can see the guest list, confirmations, invoices and check-ins you choose to share.</p>
                                <p>You decide what’s visible. Your costs and margins stay with you.</p>
                                <p className="ngh-feature-foot"><span aria-hidden="true">↳</span> A clear view for them. The controls for you.</p>
                            </div>
                            <figure className="ngh-scene ngh-client-scene"><ClientWindowStage /><figcaption>Illustrative client view</figcaption></figure>
                        </div>
                    </NgReveal>
                </section>

                <section className="ngh-section ngh-wrap" aria-labelledby="ng-details-title">
                    <p className="ngh-label">The rest of the job, covered</p>
                    <h2 id="ng-details-title" className="ngh-section-title">Behind every good event:<br />a few very good systems.</h2>
                    <div className="ngh-details">
                        <article><span className="ngh-detail-num">04</span><h3>Send the invoice.<br />Receive the naira.</h3><p>Invoice your client at your price. They pay through Paystack from a private link. Withdraw to your Nigerian bank account.</p><span className="ngh-detail-note">Your work deserves a paper trail.</span></article>
                        <article><span className="ngh-detail-num">05</span><h3>More hands.<br />Your rules.</h3><p>Add your team and set each person’s permissions. Let a junior planner manage guests and door staff handle scanning.</p><span className="ngh-detail-note">Give access to the job they do.</span></article>
                        <article><span className="ngh-detail-num">06</span><h3>Set the rule.<br />Skip the repeat.</h3><p>Build automations for guests, guest lists and clients. Send a pass after an RSVP or start the next step when a payment arrives.</p><span className="ngh-detail-note">Routine work, already handled.</span></article>
                    </div>
                </section>

                <section className="ngh-section ngh-wrap" aria-labelledby="ng-price-title">
                    <div className="ngh-price-note">
                        <span className="ngh-price-scribble" aria-hidden="true">Yes, free.</span>
                        <p className="ngh-label">A tool for your business</p>
                        <h2 id="ng-price-title">Free to run.<br /><mark>Yours to price.</mark></h2>
                        <p>No subscription or per-event fee. Buy messages and guest-collection seats at wholesale rates, then charge your client your own price.</p>
                        {isLive('pricing') && <Link to="/pricing" className="ngh-link">See how the pricing works <span aria-hidden="true">↗</span></Link>}
                    </div>
                </section>

                <section className="ngh-section ngh-wrap ngh-faq" aria-labelledby="ng-faq-title">
                    <div><p className="ngh-label">Before you ask</p><h2 id="ng-faq-title">Fair<br />questions.</h2><p>The details, without the small print.</p></div>
                    <div className="ngh-faq-list">{QA_ITEMS.map(({ q, a }, i) => <details key={q} open={i === 0}><summary>{q}<span aria-hidden="true">+</span></summary><p>{a}</p></details>)}</div>
                </section>

                <section className="ngh-final ngh-wrap" aria-labelledby="ng-final-title">
                    <span className="ngh-final-flower" aria-hidden="true">✳</span>
                    <h2 id="ng-final-title">You have an event to plan.<br />Let’s get the <mark>list sorted.</mark></h2>
                    <a href={appSignupUrl()} className="ngl-btn ngh-primary" data-cta="ng-footer-start-free">Start free <span aria-hidden="true">↗</span></a>
                    <p className="ngh-fine">Made for the people who make it happen.</p>
                </section>
            </main>
        </NgShell>
    );
};

export default NgHomePage;
